/**
 * VGrove 中间件系统
 * 基于 React Router v7+ 中间件特性的路由守卫和中间件链式处理
 */

import React from 'react';
import { redirect, type LoaderFunctionArgs, type ActionFunctionArgs, type Params } from 'react-router';
import { debug } from './debug';
import type { AuthCheck } from './loaders';
import type { BaseUser } from './types';

/**
 * 中间件执行上下文
 */
export interface MiddlewareContext<TUser extends BaseUser = BaseUser> {
  /** 请求对象 */
  request: Request;
  /** 当前用户信息 */
  user?: TUser | null;
  /** 路由参数 */
  params: Params<string>;
  /** 中间件共享数据 */
  data: Record<string, any>;
  /** 是否为 loader 调用 */
  isLoader: boolean;
  /** 是否为 action 调用 */
  isAction: boolean;
}

/**
 * 中间件执行结果
 */
export interface MiddlewareResult {
  /** 是否继续执行后续中间件 */
  continue: boolean;
  /** 重定向响应（如果需要） */
  redirect?: Response;
  /** 修改后的上下文数据 */
  data?: Record<string, any>;
  /** 错误信息 */
  error?: string;
}

/**
 * 中间件函数类型
 */
export type MiddlewareFunction<TUser extends BaseUser = BaseUser> = (
  context: MiddlewareContext<TUser>
) => Promise<MiddlewareResult> | MiddlewareResult;

/**
 * 中间件配置
 */
export interface MiddlewareConfig<TUser extends BaseUser = BaseUser> {
  /** 中间件名称 */
  name: string;
  /** 中间件函数 */
  handler: MiddlewareFunction<TUser>;
  /** 执行优先级（数字越小优先级越高） */
  priority: number;
  /** 是否启用 */
  enabled: boolean;
  /** 适用的路径模式 */
  pathPatterns?: string[];
  /** 排除的路径模式 */
  excludePatterns?: string[];
  /** 是否仅在 loader 中执行 */
  loaderOnly?: boolean;
  /** 是否仅在 action 中执行 */
  actionOnly?: boolean;
}

/**
 * 守卫失败提示配置
 */
export interface GuardFailureConfig {
  /** 提示组件类型 */
  type: 'toast' | 'modal' | 'redirect' | 'inline';
  /** 提示标题 */
  title?: string;
  /** 提示内容 */
  message: string;
  /** 动画类型 */
  animation?: 'fade' | 'slide' | 'bounce' | 'shake';
  /** 动画持续时间（毫秒） */
  duration?: number;
  /** 自动关闭时间（毫秒） */
  autoClose?: number;
  /** 重定向路径（type 为 redirect 时） */
  redirectPath?: string;
  /** 自定义样式类名 */
  className?: string;
  /** 是否可关闭 */
  closable?: boolean;
  /** 图标类型 */
  icon?: 'warning' | 'shield' | 'lock' | 'custom';
  /** 自定义图标 */
  customIcon?: React.ReactNode;
  /** 操作按钮 */
  actions?: Array<{
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'danger';
  }>;
}

/**
 * 中间件管理器
 */
export class MiddlewareManager<TUser extends BaseUser = BaseUser> {
  private middlewares: Map<string, MiddlewareConfig<TUser>> = new Map();
  private guardFailureConfigs: Map<string, GuardFailureConfig> = new Map();
  private globalFailureConfig: GuardFailureConfig = {
    type: 'toast',
    message: '访问被拒绝',
    animation: 'fade',
    duration: 300,
    autoClose: 3000
  };

  private static instance: MiddlewareManager<any> | null = null;

  // 私有构造函数防止外部实例化
  private constructor() { }

  // 获取单例实例（类型安全版本）
  public static getManager<T extends BaseUser>(): MiddlewareManager<T> {
    if (!MiddlewareManager.instance) {
      MiddlewareManager.instance = new MiddlewareManager<T>();
    }
    return MiddlewareManager.instance as MiddlewareManager<T>;
  }

  /**
   * 注册中间件
   */
  register(config: MiddlewareConfig<TUser>): void {
    debug.middleware('注册中间件', {
      name: config.name,
      priority: config.priority,
      enabled: config.enabled
    });

    this.middlewares.set(config.name, config);
  }

  /**
   * 注销中间件
   */
  unregister(name: string): void {
    debug.middleware('注销中间件', { name });
    this.middlewares.delete(name);
  }

  /**
   * 启用/禁用中间件
   */
  toggle(name: string, enabled: boolean): void {
    const middleware = this.middlewares.get(name);
    if (middleware) {
      middleware.enabled = enabled;
      debug.middleware('切换中间件状态', { name, enabled });
    }
  }

  /**
   * 配置守卫失败提示
   */
  configureGuardFailure(guardName: string, config: GuardFailureConfig): void {
    debug.middleware('配置守卫失败提示', { guardName, config });
    this.guardFailureConfigs.set(guardName, config);
  }

  /**
   * 配置全局守卫失败提示
   */
  configureGlobalGuardFailure(config: GuardFailureConfig): void {
    debug.middleware('配置全局守卫失败提示', config);
    this.globalFailureConfig = { ...this.globalFailureConfig, ...config };
  }

  /**
   * 获取守卫失败配置
   */
  private getGuardFailureConfig(guardName?: string): GuardFailureConfig {
    if (guardName && this.guardFailureConfigs.has(guardName)) {
      return this.guardFailureConfigs.get(guardName)!;
    }
    return this.globalFailureConfig;
  }

  /**
   * 检查路径是否匹配模式
   */
  private matchesPattern(path: string, patterns: string[]): boolean {
    return patterns.some(pattern => {
      if (pattern.includes('*')) {
        const regex = new RegExp(pattern.replace(/\*/g, '.*'));
        return regex.test(path);
      }
      return path === pattern || path.startsWith(pattern);
    });
  }

  /**
   * 获取适用的中间件列表
   */
  private getApplicableMiddlewares(
    pathname: string,
    isLoader: boolean,
    isAction: boolean
  ): MiddlewareConfig<TUser>[] {
    const applicable = Array.from(this.middlewares.values())
      .filter(middleware => {
        // 检查是否启用
        if (!middleware.enabled) return false;

        // 检查执行上下文
        if (middleware.loaderOnly && !isLoader) return false;
        if (middleware.actionOnly && !isAction) return false;

        // 检查路径模式
        if (middleware.pathPatterns && middleware.pathPatterns.length > 0) {
          if (!this.matchesPattern(pathname, middleware.pathPatterns)) {
            return false;
          }
        }

        // 检查排除模式
        if (middleware.excludePatterns && middleware.excludePatterns.length > 0) {
          if (this.matchesPattern(pathname, middleware.excludePatterns)) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => a.priority - b.priority); // 按优先级排序

    debug.middleware('获取适用中间件', {
      pathname,
      isLoader,
      isAction,
      applicable: applicable.map(m => ({ name: m.name, priority: m.priority }))
    });

    return applicable;
  }

  /**
   * 处理守卫失败
   */
  private handleGuardFailure(
    guardName: string,
    error: string,
    context: MiddlewareContext<TUser>
  ): Response {
    const config = this.getGuardFailureConfig(guardName);

    debug.middleware('处理守卫失败', {
      guardName,
      error,
      config,
      pathname: new URL(context.request.url).pathname
    });

    // 根据配置类型处理失败
    switch (config.type) {
      case 'redirect':
        if (config.redirectPath) {
          const url = new URL(context.request.url);
          const redirectUrl = new URL(config.redirectPath, url.origin);
          redirectUrl.searchParams.set('error', error);
          redirectUrl.searchParams.set('from', url.pathname);
          return redirect(redirectUrl.toString());
        }
        break;

      case 'toast':
      case 'modal':
      case 'inline': {
        // 这些类型需要在客户端处理，通过特殊的响应头传递配置
        const response = new Response(null, { status: 403 });
        response.headers.set('X-VGrove-Guard-Failure', JSON.stringify({
          type: config.type,
          title: config.title,
          message: config.message || error,
          animation: config.animation,
          duration: config.duration,
          autoClose: config.autoClose,
          className: config.className,
          closable: config.closable
        }));
        return response;
      }
    }

    // 默认重定向到登录页
    return redirect('/login');
  }

  /**
   * 执行中间件链
   */
  async execute(
    args: LoaderFunctionArgs | ActionFunctionArgs,
    user?: TUser | null
  ): Promise<{ success: boolean; data?: any; response?: Response }> {
    const { request, params } = args;
    const url = new URL(request.url);
    const isLoader = 'loader' in args;
    const isAction = !isLoader;

    debug.middleware('开始执行中间件链', {
      pathname: url.pathname,
      isLoader,
      isAction,
      hasUser: !!user
    });

    const applicableMiddlewares = this.getApplicableMiddlewares(
      url.pathname,
      isLoader,
      isAction
    );

    if (applicableMiddlewares.length === 0) {
      debug.middleware('无适用中间件，跳过执行');
      return { success: true };
    }

    const context: MiddlewareContext<TUser> = {
      request,
      user,
      params: params as Params<string>,
      data: {},
      isLoader,
      isAction
    };

    // 按优先级顺序执行中间件
    for (const middleware of applicableMiddlewares) {
      debug.middleware('执行中间件', {
        name: middleware.name,
        priority: middleware.priority
      });

      try {
        const result = await middleware.handler(context);

        // 更新上下文数据
        if (result.data) {
          context.data = { ...context.data, ...result.data };
        }

        // 检查是否需要中断执行
        if (!result.continue) {
          debug.middleware('中间件中断执行', {
            name: middleware.name,
            error: result.error
          });

          // 如果有重定向响应，直接返回
          if (result.redirect) {
            return { success: false, response: result.redirect };
          }

          // 如果有错误，处理守卫失败
          if (result.error) {
            const response = this.handleGuardFailure(
              middleware.name,
              result.error,
              context
            );
            return { success: false, response };
          }

          // 默认返回 403
          return {
            success: false,
            response: new Response(null, { status: 403 })
          };
        }

        debug.middleware('中间件执行成功', {
          name: middleware.name,
          hasData: !!result.data
        });

      } catch (error) {
        debug.middleware('中间件执行异常', {
          name: middleware.name,
          error: error instanceof Error ? error.message : String(error)
        });

        // 中间件异常时的处理
        const response = this.handleGuardFailure(
          middleware.name,
          error instanceof Error ? error.message : '中间件执行异常',
          context
        );
        return { success: false, response };
      }
    }

    debug.middleware('中间件链执行完成', {
      dataKeys: Object.keys(context.data)
    });

    return {
      success: true,
      data: context.data
    };
  }

  /**
   * 获取所有中间件信息
   */
  getMiddlewares(): MiddlewareConfig<TUser>[] {
    return Array.from(this.middlewares.values())
      .sort((a, b) => a.priority - b.priority);
  }

  /**
   * 清空所有中间件
   */
  clear(): void {
    debug.middleware('清空所有中间件');
    this.middlewares.clear();
    this.guardFailureConfigs.clear();
  }
}

// 全局中间件管理器实例
export const middlewareManager = MiddlewareManager.getManager<BaseUser>();

// ============= VGrove Autoroutes 集成函数 =============

/**
 * 为 vgrove-autoroutes 生成的路由初始化中间件
 * 此函数会被 vgrove-autoroutes 在生成路由代码时自动调用
 */
export function initializeMiddlewares(): void {
  // 这个函数会被 vgrove-autoroutes 的 parser.ts 中的 generateMiddlewareManagerCode 调用
  // 不需要在这里实现具体逻辑，因为实际的中间件注册由 autoroutes 生成的代码处理
}

/**
 * 检查中间件管理器是否已正确初始化
 */
export function isMiddlewareManagerReady(): boolean {
  return middlewareManager.getMiddlewares().length > 0;
}

/**
 * 获取适用于特定路径的中间件列表
 * 用于调试和路由配置验证
 */
export function getMiddlewaresForPath(pathname: string): MiddlewareConfig<BaseUser>[] {
  return middlewareManager.getMiddlewares().filter(middleware => {
    // 检查路径模式
    if (middleware.pathPatterns && middleware.pathPatterns.length > 0) {
      return middleware.pathPatterns.some(pattern => {
        if (pattern === '*') return true;
        if (pattern === pathname) return true;
        
        const regexPattern = pattern
          .replace(/\*/g, '.*')
          .replace(/\//g, '\\/');
        const regex = new RegExp(`^${regexPattern}$`);
        return regex.test(pathname);
      });
    }
    
    // 检查排除模式
    if (middleware.excludePatterns && middleware.excludePatterns.length > 0) {
      return !middleware.excludePatterns.some(pattern => {
        const regexPattern = pattern
          .replace(/\*/g, '.*')
          .replace(/\//g, '\\/');
        const regex = new RegExp(`^${regexPattern}$`);
        return regex.test(pathname);
      });
    }
    
    return true;
  });
}

// ============= 预定义中间件 =============

/**
 * 认证中间件
 */
export function createAuthMiddleware<TUser extends BaseUser = BaseUser>(
  authCheck: AuthCheck<TUser>,
  options: {
    priority?: number;
    pathPatterns?: string[];
    excludePatterns?: string[];
    failureConfig?: GuardFailureConfig;
  } = {}
): MiddlewareConfig<TUser> {
  return {
    name: 'auth',
    priority: options.priority || 100,
    enabled: true,
    pathPatterns: options.pathPatterns,
    excludePatterns: options.excludePatterns,
    handler: async (context) => {
      debug.middleware('执行认证检查');

      const user = await authCheck();

      if (!user) {
        return {
          continue: false,
          error: '用户未认证，请先登录'
        };
      }

      // 更新上下文中的用户信息
      context.user = user;

      return {
        continue: true,
        data: { user }
      };
    }
  };
}

/**
 * 角色检查中间件
 */
export function createRoleMiddleware<TUser extends BaseUser = BaseUser>(
  requiredRoles: string[],
  options: {
    priority?: number;
    pathPatterns?: string[];
    excludePatterns?: string[];
    requireAll?: boolean;
    failureConfig?: GuardFailureConfig;
  } = {}
): MiddlewareConfig<TUser> {
  return {
    name: `role-${requiredRoles.join('-')}`,
    priority: options.priority || 200,
    enabled: true,
    pathPatterns: options.pathPatterns,
    excludePatterns: options.excludePatterns,
    handler: async (context) => {
      debug.middleware('执行角色检查', { requiredRoles });

      if (!context.user) {
        return {
          continue: false,
          error: '用户信息不存在，无法进行角色检查'
        };
      }

      const userRoles = context.user.roles || [];
      const hasRole = options.requireAll
        ? requiredRoles.every(role => userRoles.includes(role))
        : requiredRoles.some(role => userRoles.includes(role));

      if (!hasRole) {
        return {
          continue: false,
          error: `需要角色: ${requiredRoles.join(', ')}`
        };
      }

      return {
        continue: true,
        data: { roleCheck: true }
      };
    }
  };
}

/**
 * 权限检查中间件
 */
export function createPermissionMiddleware<TUser extends BaseUser = BaseUser>(
  requiredPermissions: string[],
  options: {
    priority?: number;
    pathPatterns?: string[];
    excludePatterns?: string[];
    requireAll?: boolean;
    failureConfig?: GuardFailureConfig;
  } = {}
): MiddlewareConfig<TUser> {
  return {
    name: `permission-${requiredPermissions.join('-')}`,
    priority: options.priority || 300,
    enabled: true,
    pathPatterns: options.pathPatterns,
    excludePatterns: options.excludePatterns,
    handler: async (context) => {
      debug.middleware('执行权限检查', { requiredPermissions });

      if (!context.user) {
        return {
          continue: false,
          error: '用户信息不存在，无法进行权限检查'
        };
      }

      const userPermissions = context.user.permissions || [];
      const hasPermission = options.requireAll
        ? requiredPermissions.every(permission => userPermissions.includes(permission))
        : requiredPermissions.some(permission => userPermissions.includes(permission));

      if (!hasPermission) {
        return {
          continue: false,
          error: `需要权限: ${requiredPermissions.join(', ')}`
        };
      }

      return {
        continue: true,
        data: { permissionCheck: true }
      };
    }
  };
}

/**
 * 速率限制中间件
 */
export function createRateLimitMiddleware<TUser extends BaseUser = BaseUser>(
  options: {
    maxRequests: number;
    windowMs: number;
    priority?: number;
    pathPatterns?: string[];
    excludePatterns?: string[];
    keyGenerator?: (context: MiddlewareContext<TUser>) => string;
  }
): MiddlewareConfig<TUser> {
  const requests = new Map<string, { count: number; resetTime: number }>();

  return {
    name: 'rate-limit',
    priority: options.priority || 50,
    enabled: true,
    pathPatterns: options.pathPatterns,
    excludePatterns: options.excludePatterns,
    handler: async (context) => {
      const key = options.keyGenerator
        ? options.keyGenerator(context)
        : context.user?.id?.toString() || context.request.headers.get('x-forwarded-for') || 'anonymous';

      const now = Date.now();
      const record = requests.get(key);

      if (!record || now > record.resetTime) {
        // 重置或创建新记录
        requests.set(key, {
          count: 1,
          resetTime: now + options.windowMs
        });
        return { continue: true };
      }

      if (record.count >= options.maxRequests) {
        return {
          continue: false,
          error: '请求过于频繁，请稍后再试'
        };
      }

      record.count++;
      return { continue: true };
    }
  };
}

// ============= 便捷函数 =============

/**
 * 注册认证中间件
 */
export function registerAuthMiddleware<TUser extends BaseUser = BaseUser>(
  authCheck: AuthCheck<TUser>,
  options?: Parameters<typeof createAuthMiddleware<TUser>>[1]
): void {
  const middleware = createAuthMiddleware<TUser>(authCheck, options);
  (middlewareManager as any).register(middleware);

  // 配置失败提示
  if (options?.failureConfig) {
    middlewareManager.configureGuardFailure('auth', options.failureConfig);
  }
}

/**
 * 注册角色中间件
 */
export function registerRoleMiddleware<TUser extends BaseUser = BaseUser>(
  requiredRoles: string[],
  options?: Parameters<typeof createRoleMiddleware<TUser>>[1]
): void {
  const middleware = createRoleMiddleware<TUser>(requiredRoles, options);
  (middlewareManager as any).register(middleware);

  // 配置失败提示
  if (options?.failureConfig) {
    middlewareManager.configureGuardFailure(middleware.name, options.failureConfig);
  }
}

/**
 * 注册权限中间件
 */
export function registerPermissionMiddleware<TUser extends BaseUser = BaseUser>(
  requiredPermissions: string[],
  options?: Parameters<typeof createPermissionMiddleware<TUser>>[1]
): void {
  const middleware = createPermissionMiddleware<TUser>(requiredPermissions, options);
  (middlewareManager as any).register(middleware);

  // 配置失败提示
  if (options?.failureConfig) {
    middlewareManager.configureGuardFailure(middleware.name, options.failureConfig);
  }
}

/**
 * 注册速率限制中间件
 */
export function registerRateLimitMiddleware<TUser extends BaseUser = BaseUser>(
  options: Parameters<typeof createRateLimitMiddleware<TUser>>[0]
): void {
  const middleware = createRateLimitMiddleware<TUser>(options);
  (middlewareManager as any).register(middleware);
}

/**
 * 创建集成中间件的 loader
 */
export function createMiddlewareLoader(
  originalLoader?: (args: LoaderFunctionArgs) => any
) {
  return async (args: LoaderFunctionArgs) => {
    // 执行中间件链
    const middlewareResult = await middlewareManager.execute(args);

    if (!middlewareResult.success) {
      if (middlewareResult.response) {
        throw middlewareResult.response;
      }
      throw new Response(null, { status: 403 });
    }

    // 执行原始 loader
    let loaderResult = null;
    if (originalLoader) {
      loaderResult = await originalLoader(args);
    }

    // 合并中间件数据和 loader 结果
    return {
      ...middlewareResult.data,
      ...loaderResult
    };
  };
}

/**
 * 创建集成中间件的 action
 */
export function createMiddlewareAction(
  originalAction?: (args: ActionFunctionArgs) => any
) {
  return async (args: ActionFunctionArgs) => {
    // 执行中间件链
    const middlewareResult = await middlewareManager.execute(args);

    if (!middlewareResult.success) {
      if (middlewareResult.response) {
        throw middlewareResult.response;
      }
      throw new Response(null, { status: 403 });
    }

    // 执行原始 action
    if (originalAction) {
      return await originalAction(args);
    }

    return { success: true, data: middlewareResult.data };
  };
}